/**
 * activeProject.tsx — which project the user is currently looking at.
 *
 * The AI rail lives in the AppShell, but only a PAGE knows which project is on
 * screen: /audit resolves to the most-recently-updated project when the route
 * carries no id, so the shell cannot derive it from the URL. Rather than
 * duplicating that resolution (and letting the two copies drift), the page
 * publishes what it resolved and the shell reads it.
 *
 * Publishing is keyed on primitives, never on an object literal — an effect
 * depending on `{id, name}` would re-fire every render and loop.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export interface ActiveProject {
  id: string;
  name: string;
}

interface ActiveProjectValue {
  project: ActiveProject | null;
  publish: (project: ActiveProject | null) => void;
}

const ActiveProjectContext = createContext<ActiveProjectValue>({
  project: null,
  publish: () => {},
});

export function ActiveProjectProvider({ children }: { children: ReactNode }): JSX.Element {
  const [project, setProject] = useState<ActiveProject | null>(null);
  const value = useMemo<ActiveProjectValue>(() => ({ project, publish: setProject }), [project]);
  return <ActiveProjectContext.Provider value={value}>{children}</ActiveProjectContext.Provider>;
}

/** Read the active project. Returns null on pages that publish nothing. */
export function useActiveProject(): ActiveProject | null {
  return useContext(ActiveProjectContext).project;
}

/**
 * Publish the project this page resolved. Pass nulls while loading — the rail
 * then shows its placeholder rather than a stale project's name.
 *
 * Clears on unmount so navigating away can never leave another screen's
 * assistant pointed at this project.
 */
export function usePublishActiveProject(id: string | null, name: string | null): void {
  const { publish } = useContext(ActiveProjectContext);
  useEffect(() => {
    publish(id && name ? { id, name } : null);
    return () => publish(null);
  }, [id, name, publish]);
}
